/**
 * The ONE canonical vocabulary for "a thing that happened" (ADR-0039) — the single source of truth that
 * keeps EVERY history surface (kid My Story · parent per-child history · family feed · wallet ledger ·
 * recent wins · admin economy log) consistent BY CONSTRUCTION. Both data models — the activity projection
 * (`ActivityItemResponse`) and the coin ledger (`LedgerEntryResponse`) — map their own enums onto these
 * concepts, then render through the shared `HistoryRow`. Change a glyph, a tone, or a label HERE and every
 * surface updates at once; nothing can drift.
 *
 * A `HistoryConcept` is the meaningful EVENT type (a chore reward, a redeemed reward, a mastered skill…).
 * Each carries a glyph (locale-independent, lives in code), a calm tone (`caution` is muted amber, never a
 * punitive red), and an i18n label key (`common:history.kind.<concept>`). Raw names (chore/reward titles)
 * and translatable subjects (skill names) ride the row's source slot, never these labels.
 */

import { conceptArt } from '@/assets/icons';

export type HistoryTone = 'positive' | 'neutral' | 'caution';

export type HistoryConcept =
  | 'chore_completed'
  | 'chore_approved'
  | 'chore_rejected'
  | 'chore_skipped'
  | 'chore_backfilled'
  | 'chore_reward'
  | 'habit_checked_in'
  | 'quest_completed'
  | 'reward_requested'
  | 'reward_granted'
  | 'reward_denied'
  | 'reward_redeemed'
  | 'coins_earned'
  | 'coins_spent'
  | 'stars_earned'
  | 'stars_spent'
  | 'cash_added'
  | 'cash_removed'
  | 'allowance'
  | 'interest'
  | 'adjustment'
  | 'reversal'
  | 'cosmetic'
  | 'mastered'
  | 'daily_goal'
  | 'achievement'
  | 'learning_practiced'
  | 'privilege_paused'
  | 'privilege_resumed';

interface ConceptMeta {
  readonly glyph: string;
  readonly tone: HistoryTone;
  /** Concept icon art URL (the single source; rendered with `glyph` as the emoji fallback). */
  readonly art: string;
}

/** The single registry: concept → glyph + tone. The label is `common:history.kind.<concept>`. */
export const HISTORY_CONCEPT: Record<HistoryConcept, ConceptMeta> = {
  chore_completed: { glyph: '✅', tone: 'positive', art: conceptArt('check') },
  chore_approved: { glyph: '🌟', tone: 'positive', art: conceptArt('gold-star') },
  chore_rejected: { glyph: '↩️', tone: 'caution', art: conceptArt('undo') },
  chore_skipped: { glyph: '⏭️', tone: 'neutral', art: conceptArt('skip') },
  chore_backfilled: { glyph: '🗓️', tone: 'positive', art: conceptArt('calendar-check') },
  chore_reward: { glyph: '✅', tone: 'positive', art: conceptArt('check') },
  habit_checked_in: { glyph: '🔥', tone: 'positive', art: conceptArt('flame') },
  quest_completed: { glyph: '🏆', tone: 'positive', art: conceptArt('trophy') },
  reward_requested: { glyph: '🙋', tone: 'neutral', art: conceptArt('raise-hand') },
  reward_granted: { glyph: '🎁', tone: 'positive', art: conceptArt('gift') },
  reward_denied: { glyph: '🚫', tone: 'caution', art: conceptArt('no-entry') },
  reward_redeemed: { glyph: '🎁', tone: 'neutral', art: conceptArt('gift') },
  coins_earned: { glyph: '✨', tone: 'positive', art: conceptArt('coin-sparkle') },
  coins_spent: { glyph: '🛍️', tone: 'neutral', art: conceptArt('shopping-bag') },
  stars_earned: { glyph: '⭐', tone: 'positive', art: conceptArt('gold-star') },
  stars_spent: { glyph: '⭐', tone: 'neutral', art: conceptArt('gold-star') },
  cash_added: { glyph: '💵', tone: 'positive', art: conceptArt('money-bag') },
  cash_removed: { glyph: '💵', tone: 'neutral', art: conceptArt('money-bag') },
  allowance: { glyph: '💰', tone: 'positive', art: conceptArt('money-bag') },
  interest: { glyph: '📈', tone: 'positive', art: conceptArt('chart-up') },
  adjustment: { glyph: '⚖️', tone: 'neutral', art: conceptArt('scale') },
  reversal: { glyph: '↩️', tone: 'caution', art: conceptArt('undo') },
  cosmetic: { glyph: '🎨', tone: 'positive', art: conceptArt('palette') },
  mastered: { glyph: '🧠', tone: 'positive', art: conceptArt('brain') },
  daily_goal: { glyph: '🎯', tone: 'positive', art: conceptArt('target') },
  achievement: { glyph: '🏅', tone: 'positive', art: conceptArt('medal') },
  learning_practiced: { glyph: '📚', tone: 'positive', art: conceptArt('learning') },
  // A pause is calm management, never punitive (no shaming); a resume is a small positive.
  privilege_paused: { glyph: '⏸️', tone: 'neutral', art: conceptArt('pause') },
  privilege_resumed: { glyph: '▶️', tone: 'positive', art: conceptArt('resume') },
};

/**
 * The i18n label key for a concept within the `common` namespace, e.g. `history.kind.mastered`. Resolve it
 * with a `common`-bound `t` (every row gets one) so the feed and the ledger show the identical label.
 */
export function conceptLabelKey(concept: HistoryConcept): string {
  return `history.kind.${concept}`;
}

/**
 * Map an ACTIVITY-feed kind onto a concept. A chore reward / reward redemption ride the generic
 * `points_earned` / `points_spent` (they FOLD into their lifecycle row — chore approved / reward granted —
 * so the generic money kind is only ever shown standalone in the rare uncorrelated case). Every other
 * earn carries its source kind (daily goal, streak bonus, allowance, interest, mastery, cosmetic), so the
 * feed reads the same specific label as the wallet ledger.
 */
export function conceptFromActivityKind(kind: string): HistoryConcept {
  switch (kind) {
    case 'chore_completed':
      return 'chore_completed';
    case 'chore_approved':
      return 'chore_approved';
    case 'chore_rejected':
      return 'chore_rejected';
    case 'chore_skipped':
      return 'chore_skipped';
    case 'backfill_logged':
      return 'chore_backfilled';
    case 'habit_checked_in':
      return 'habit_checked_in';
    case 'quest_completed':
      return 'quest_completed';
    case 'reward_requested':
      return 'reward_requested';
    case 'reward_granted':
      return 'reward_granted';
    case 'reward_denied':
      return 'reward_denied';
    case 'points_earned':
      return 'coins_earned';
    case 'points_spent':
      return 'coins_spent';
    case 'allowance_paid':
      return 'allowance';
    case 'interest_paid':
      return 'interest';
    case 'adjustment_posted':
      return 'adjustment';
    case 'reversal_posted':
      return 'reversal';
    case 'cosmetic_unlocked':
      return 'cosmetic';
    case 'learning_mastered':
      return 'mastered';
    case 'daily_goal_reward':
      return 'daily_goal';
    case 'achievement_bonus':
      return 'achievement';
    case 'learning_practiced':
      return 'learning_practiced';
    case 'privilege_paused':
      return 'privilege_paused';
    case 'privilege_resumed':
      return 'privilege_resumed';
    default:
      return 'coins_earned';
  }
}

/** Map a LEDGER detail kind onto a concept — source-aware (a chore reward, a redeemed reward, a mastery…). */
export function conceptFromLedgerKind(kind: string, currency?: string, amount = 0): HistoryConcept {
  switch (kind) {
    case 'chore':
      return 'chore_reward';
    case 'reward':
      return 'reward_redeemed';
    case 'allowance':
      return 'allowance';
    case 'interest':
      return 'interest';
    case 'adjustment':
      return 'adjustment';
    case 'reversal':
      return 'reversal';
    case 'cosmetic':
      return 'cosmetic';
    case 'learning_mastery':
      return 'mastered';
    case 'learning_daily_goal':
      return 'daily_goal';
    case 'achievement_bonus':
      return 'achievement';
    case 'effort_star_earned':
      return 'stars_earned';
    case 'effort_star_spent':
      return 'stars_spent';
    default:
      // Currency-aware fallback for an unmapped kind — NEVER call a cash (₴) or star (✨) movement
      // "coins": a +0.08 ₴ interest/adjustment row read as "Coins earned" before this. Pick the label
      // by the entry's currency + sign so the kind label always matches the amount shown.
      if (currency === 'cash') return amount >= 0 ? 'cash_added' : 'cash_removed';
      if (currency === 'star') return amount >= 0 ? 'stars_earned' : 'stars_spent';
      return amount >= 0 ? 'coins_earned' : 'coins_spent';
  }
}
